// 레이드 이벤트 → 레이드 절차 효과음 결정(순수). WebAudio를 모른다. 미리 할당한 RaidCueList에 쓴다(프레임 루프 할당 없음).
// 게임 이벤트를 새로 만들지 않는다: 기존 Ev.BossIntro·BossPattern·TelegraphFire·BossDefeated·GuardBreak·RaidMerc·Hit(Ally)·StageClear·Defeat와
// 상태 변화(브레스 불길 틱·공중 여부·불 장판, engine.ts)로만 판단한다.
// 파일 효과음(../decide.ts)·c019 절차음(../synth/decide.ts)은 isWyvernEvent / raidOwnsJingle로 같은 순간 소리를 겹치지 않는다.
import { Ev, HitFlag, TeleSource, type GameEvent } from '../../shared/events';
import { MERCS, WYVERN, WyvernPattern, type MercRole } from '../../shared/content/raid';
import { BossKind } from '../../shared/types';
import { RAID_DEFS, RY } from './recipes';

export interface RaidCue {
  id: number;
  gainDb: number;
  /** 재생 속도(피치) 배율 */
  rate: number;
  x: number;
  z: number;
  positional: boolean;
  /** 지금부터 미뤄 시작(초) */
  delay: number;
}

export class RaidCueList {
  readonly items: RaidCue[];
  count = 0;
  constructor(capacity = 4) {
    this.items = [];
    for (let i = 0; i < capacity; i++) this.items.push({ id: 0, gainDb: 0, rate: 1, x: 0, z: 0, positional: false, delay: 0 });
  }
  clear(): void { this.count = 0; }
  add(id: number, gainDb = 0, rate = 1, x = 0, z = 0, delay = 0): RaidCue | null {
    if (this.count >= this.items.length) return null;
    const c = this.items[this.count++];
    c.id = id; c.gainDb = gainDb; c.rate = rate; c.x = x; c.z = z; c.delay = delay;
    c.positional = RAID_DEFS[id].positional;
    return c;
  }
}

export interface RaidDecideState {
  /** 마지막 공격 패턴(WyvernPattern 0..6, -1 없음) — 판정(TelegraphFire)이 어느 패턴 것인지 고른다 */
  lastPattern: number;
}

export function newRaidDecideState(): RaidDecideState { return { lastPattern: -1 }; }

/** 직업 AI 역할별 행동음(지금 방패병·궁수·사제. CHARACTER-PRD 직업 5가 들어오면 줄을 더한다). 모르는 역할은 방패병 소리 */
export interface RoleSounds { readonly attack: number; readonly hit: number; readonly hitDelay: number }
export const ROLE_SOUNDS: Readonly<Record<MercRole, RoleSounds>> = {
  tank: { attack: RY.mercSwing, hit: -1, hitDelay: 0 }, // 방패병 명중은 파일 hitBoss(-4dB)가 맡는다
  archer: { attack: RY.mercBow, hit: RY.mercArrowHit, hitDelay: 0.09 },
  priest: { attack: RY.mercHolyBolt, hit: RY.mercHolyHit, hitDelay: 0.12 },
};
export function roleOf(slot: number): MercRole {
  return MERCS[slot]?.role ?? 'tank';
}
function roleSounds(slot: number): RoleSounds { return ROLE_SOUNDS[roleOf(slot)] ?? ROLE_SOUNDS.tank; }

/** RaidMerc b 값(game/raid MercEvent와 같은 번호. 오디오는 game/을 가져오지 않는다) */
export const MercEv = { Attack: 0, Heal: 1, Taunt: 2, Down: 3, Revive: 4, Hurt: 5, Dodge: 6 } as const;

/** 비룡 패턴 경고음(BossPattern a → 소리, -1 없음) */
export const WYV_TELE: Readonly<Record<number, number>> = {
  [WyvernPattern.bite]: RY.teleWyvBite, [WyvernPattern.tail]: RY.teleWyvTail, [WyvernPattern.pools]: RY.teleWyvPools,
  [WyvernPattern.charge]: RY.teleWyvCharge, [WyvernPattern.takeoff]: RY.wyvTakeoff, [WyvernPattern.breath]: RY.wyvInhale,
  [WyvernPattern.land]: RY.teleWyvLand, [WyvernPattern.phase2]: RY.wyvRoarPhase, [WyvernPattern.enrage]: RY.wyvRoarEnrage,
  // 하드 잿불 낙인 시작(c ≥ 0 = 붙은 칸 비트). 폭발(c = -1)은 decideRaid가 wyvBrandBlast로 따로 낸다
  [WyvernPattern.brand]: RY.wyvBrandMark,
};
/** 비룡 판정음(마지막 공격 패턴 → 소리, -1 없음). 브레스 불길은 판정이 아니라 렌더 불꽃 틱에 낸다(breathFireCrossed) */
export const WYV_STRIKE: Readonly<Record<number, number>> = {
  [WyvernPattern.bite]: RY.wyvBite, [WyvernPattern.tail]: RY.wyvTail, [WyvernPattern.pools]: RY.wyvFireBurst,
  [WyvernPattern.charge]: RY.wyvCharge, [WyvernPattern.land]: RY.wyvLand,
};

/** 이 이벤트가 비룡 몸·패턴 소리인가(파일·c019 결정이 건너뛴다) */
export function isWyvernEvent(e: GameEvent, bossKind: number): boolean {
  switch (e.type) {
    case Ev.BossIntro: case Ev.BossDefeated: return e.b === BossKind.Wyvern || bossKind === BossKind.Wyvern;
    case Ev.BossPattern: return e.b === BossKind.Wyvern;
    case Ev.TelegraphFire: return e.c === TeleSource.Boss && e.d === BossKind.Wyvern;
    default: return false;
  }
}

/** 레이드 판의 클리어·전멸은 레이드 징글이 맡는다(파일 stageClear·defeat를 건너뛴다) */
export function raidOwnsJingle(e: GameEvent, raidActive: boolean): boolean {
  return raidActive && (e.type === Ev.StageClear || e.type === Ev.Defeat);
}

/** 이벤트 하나 → 레이드 소리 0~2개. raidActive = 지금 레이드 판(view.raid.active) */
export function decideRaid(e: GameEvent, out: RaidCueList, st: RaidDecideState, raidActive: boolean): void {
  switch (e.type) {
    case Ev.BossIntro:
      if (e.b === BossKind.Wyvern) { out.add(RY.wyvRoar, 0, 1, e.x, e.z); st.lastPattern = -1; }
      break;
    case Ev.BossPattern: {
      if (e.b !== BossKind.Wyvern) break;
      const a = e.a | 0;
      // 잿불 낙인 폭발(c = -1, x/z = 폭발 자리, d = 주인 칸): 경고음이 아니라 폭발음
      if (a === WyvernPattern.brand && e.c < 0) { out.add(RY.wyvBrandBlast, 0, 1, e.x, e.z); break; }
      const id = WYV_TELE[a];
      // 4인 넷 판은 낙인 원마다 시작 사건이 따로 온다(c = 그 주인 비트, 남의 원이면 0): 남의 원은 조금 작게
      if (id !== undefined) out.add(id, a === WyvernPattern.brand && (e.c | 0) === 0 ? -4 : 0, 1, e.x, e.z);
      // 낙인도 마지막 공격 패턴으로 기억한다: 낙인 경고의 TelegraphFire가 앞 패턴(물기 등) 판정음을 내지 않게(폭발음은 위에서)
      if ((a >= WyvernPattern.bite && a <= WyvernPattern.land) || a === WyvernPattern.brand) st.lastPattern = a;
      break;
    }
    case Ev.TelegraphFire: {
      if (e.c !== TeleSource.Boss || e.d !== BossKind.Wyvern) break;
      const id = WYV_STRIKE[st.lastPattern];
      if (id !== undefined) out.add(id, 0, 1, e.x, e.z);
      break;
    }
    case Ev.BossDefeated:
      if (e.b === BossKind.Wyvern) out.add(RY.wyvDeath, 0, 1, e.x, e.z);
      break;
    case Ev.GuardBreak:
      // 레이드 판의 가드 브레이크 = 비룡 경직(파일 막힘+마무리 겹침 위에 비명을 얹는다)
      if (raidActive) out.add(RY.wyvStagger, 0, 1, e.x, e.z);
      break;
    case Ev.RaidMerc: {
      const slot = e.a | 0, k = e.b | 0;
      if (k === MercEv.Attack) out.add(roleSounds(slot).attack, 0, 1, e.x, e.z);
      else if (k === MercEv.Heal) out.add(RY.mercHeal, 0, 1, e.x, e.z);
      else if (k === MercEv.Taunt) out.add(RY.mercTaunt, 0, 1, e.x, e.z);
      else if (k === MercEv.Down) out.add(RY.mercDown, 0, 1, e.x, e.z);
      else if (k === MercEv.Revive) out.add(RY.mercRevive, 0, 1, e.x, e.z);
      else if (k === MercEv.Dodge) out.add(RY.mercDodge, 0, 1, e.x, e.z);
      break;
    }
    case Ev.Hit: {
      // 용병이 보스를 친 타격(b 1, c 용병 칸 1..3): 궁수 화살·사제 빛 명중(발사음보다 조금 늦게 — 두 소리가 뭉개지지 않게)
      if (e.b !== 1 || ((e.flags | 0) & HitFlag.Ally) === 0) break;
      const r = roleSounds((e.c | 0) - 1);
      if (r.hit >= 0) out.add(r.hit, 0, 1, e.x, e.z, r.hitDelay);
      break;
    }
    case Ev.StageClear:
      if (raidActive) out.add(RY.raidVictory);
      break;
    case Ev.Defeat:
      if (raidActive) out.add(RY.raidWipe);
      break;
    default:
      break;
  }
}

/**
 * 브레스 불길 시작 틱을 지났는가(렌더 wyvern.ts와 같은 틱: 줄 k마다 k·breathGap + breathWindup - 34).
 * prev → now는 view.boss.stateTick(비행 2단계에서 0부터). 세계 시간 배율(히트스톱·각성)에 그대로 따라간다.
 */
export const BREATH_LEAD = 34;
export function breathFireCrossed(prev: number, now: number): boolean {
  if (now <= prev) return false;
  const F = WYVERN.flight;
  for (let k = 0; k < F.breaths; k++) {
    const s0 = k * F.breathGap + F.breathWindup - BREATH_LEAD;
    if (prev < s0 && now >= s0) return true;
  }
  return false;
}

/**
 * 잿불 낙인 카운트다운 틱(순수): 남은 틱이 prev → now로 줄며 틱 자리를 지났으면 그 자리(남은 틱), 아니면 -1.
 * 틱 자리: 남은 시간이 1.5초(fastFrom)보다 길면 slow(0.5초)마다, 1.5초부터는 fast(0.2초)마다(90·78·…·6). 0(폭발)은 틱이 아니다.
 */
export const BRAND_TICK = { slow: 30, fast: 12, fastFrom: 90 } as const;
export function isBrandTick(k: number): boolean {
  const T = BRAND_TICK;
  if (k <= 0) return false;
  return k > T.fastFrom ? k % T.slow === 0 : (T.fastFrom - k) % T.fast === 0;
}
export function brandTickAt(prev: number, now: number): number {
  if (!(now < prev) || prev <= 0) return -1;
  // now 이상 prev 미만에서 가장 큰 틱 자리(프레임 하나에 보통 1~2틱이라 짧다)
  for (let k = Math.ceil(prev) - 1; k >= Math.max(1, Math.ceil(now)); k--) if (isBrandTick(k)) return k;
  return -1;
}
/** 틱 자리 → 재생 피치(마지막 1.5초는 반음 셋 높게) */
export function brandTickRate(left: number): number { return left <= BRAND_TICK.fastFrom ? 1.19 : 1; }
